import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { format } from 'date-fns'
import api from '../services/api'
import { useAsk } from '../context/PromptProvider'
import { parseDateOnly, roundBillable } from '../utils/format'
import { toFirmWallClock } from '../utils/eventTime'
import useFirmTimezone from '../hooks/useFirmTimezone'
import { recentClientsTracker } from '../lib/recentItems'
import { SkeletonCard, SkeletonTable, LoadingRegion } from '../components/Skeleton'
import useTimerEntrySaved from '../hooks/useTimerEntrySaved'
import CopyLinkButton from '../components/CopyLinkButton'
import usePageTitle from '../hooks/usePageTitle'
import useFieldIds from '../hooks/useFieldIds'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import useModal from '../hooks/useModal'
import { displayClientName } from '../lib/displayClient'


// An event's start_time is a naive-UTC instant: its day is the FIRM's
// (toFirmWallClock), the day the Calendar puts it on — never the browser's,
// which moved a 5 PM Pacific hearing to the next day for anyone east of the
// firm (James, 2026-10-17; ClientDetail.firmDay.test.js).  '' for a missing or
// invalid value: date-fns format throws on an Invalid Date.
const fmtFirmClock = (value, pattern) => {
  const d = value ? toFirmWallClock(value) : null
  return d instanceof Date && !isNaN(d.getTime()) ? format(d, pattern) : ''
}

export default function ClientDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  // The firm zone for the event dates below — fetched when this page is a
  // pop-out (PopupLayout never sets it), re-rendering once it is known.
  useFirmTimezone()
  const ask = useAsk()
  const isAdminUser = user?.role === 'admin' || user?.permissions?.is_admin === true
  const canViewTrust = isAdminUser || user?.permissions?.trust === true
  const canViewAll = isAdminUser || user?.permissions?.can_view_all_time === true
  const canViewInvoices = isAdminUser || user?.permissions?.invoices === true
  const canViewTimeEntries = isAdminUser || user?.permissions?.time_entries === true
  const canViewCalendar = isAdminUser || user?.permissions?.calendar === true
  const navigate = useNavigate()
  // WCAG 2.1 AA — scoped ids for label htmlFor association in the edit form.
  const fid = useFieldIds()
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  // Browser-tab title reflects the client's display name once loaded so
  // multi-window / multi-tab UX is scannable at a glance.
  const clientDisplayName = client
    ? (client.client_type === 'company'
        ? client.company_name
        : `${client.first_name || ''} ${client.last_name || ''}`.trim())
    : null
  usePageTitle(clientDisplayName ? `Client — ${clientDisplayName}` : 'Client')
  const [newNote, setNewNote] = useState('')
  const [editingNote, setEditingNote] = useState(null)
  const [editNoteContent, setEditNoteContent] = useState('')
  const [error, setError] = useState('')
  const [showEditClient, setShowEditClient] = useState(false)
  const [showAllTime, setShowAllTime] = useState(false)
  const [editForm, setEditForm] = useState({})

  useEffect(() => { fetchClient() }, [id])
  useTimerEntrySaved(() => fetchClient())

  usePageShortcut('m', () => navigate(`/matters?client=${id}&new=1`), { enabled: true })
  usePageShortcut('e', () => navigate(`/communications?client=${id}&new=1`), { enabled: true })

  const fetchClient = async () => {
    try {
      const res = await api.get(`/api/clients/${id}`)
      setClient(res.data)
      // Track for the sidebar "Recent Clients" muscle-memory shortcut.
      // Prefer company_name for entity clients; otherwise "First Last".
      const clientLabel = res.data.company_name?.trim()
        || `${res.data.first_name || ''} ${res.data.last_name || ''}`.trim()
        || `Client #${res.data.id}`
      recentClientsTracker.push({ id: res.data.id, title: clientLabel })
      setEditForm({
        first_name: res.data.first_name,
        last_name: res.data.last_name,
        email: res.data.email || '',
        phone: res.data.phone || '',
        secondary_phone: res.data.secondary_phone || '',
        secondary_email: res.data.secondary_email || '',
        company_name: res.data.company_name || '',
        client_type: res.data.client_type || 'individual',
        address: res.data.address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        zip: res.data.zip || '',
        referred_by: res.data.referred_by || '',
        notes: res.data.notes || '',
        status: res.data.status || 'active'
      })
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading client')
    } finally {
      setLoading(false)
    }
  }

  const surfaceError = (err, fallback) => {
    if (err?.response?.data?.error) {
      setError(err.response.data.error)
    } else if (err?.response?.status >= 500) {
      setError('Server error — please retry in a moment.')
    } else if (!err?.response) {
      setError('Cannot reach server — check your connection and retry.')
    } else {
      setError(`${fallback} (HTTP ${err.response.status})`)
    }
  }

  const handleSaveClient = async (e) => {
    e.preventDefault()
    try {
      await api.put(`/api/clients/${id}`, editForm)
      setShowEditClient(false)
      fetchClient()
    } catch (err) {
      surfaceError(err, 'Error updating client')
    }
  }

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!newNote.trim()) return
    try {
      await api.post('/api/notes', {
        content: newNote,
        client_id: id
      })
      setNewNote('')
      fetchClient()
    } catch (err) {
      surfaceError(err, 'Error adding note')
    }
  }

  const handleUpdateNote = async (noteId) => {
    try {
      await api.put(`/api/notes/${noteId}`, { content: editNoteContent })
      setEditingNote(null)
      fetchClient()
    } catch (err) {
      surfaceError(err, 'Error updating note')
    }
  }

  const handleDeleteNote = async (noteId) => {
    if (!await ask({ type: 'confirm', title: 'Delete note?', message: 'Delete this note?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/notes/${noteId}`)
      fetchClient()
    } catch (err) {
      surfaceError(err, 'Error deleting note')
    }
  }

  if (loading) return (
    <LoadingRegion label="Loading client">
      <div className="mb-4"><div className="h-8 w-64 bg-gray-100 rounded animate-pulse" /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <SkeletonTable rows={5} cols={4} />
      </div>
    </LoadingRegion>
  )

  if (!client) return (
    <div className="text-center py-12">
      <p className="text-gray-500">Client not found.</p>
      <button onClick={() => navigate('/clients')}
        className="mt-4 text-primary-600 hover:underline">
        Back to Clients
      </button>
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate('/clients')}
            className="text-gray-400 hover:text-gray-600 text-sm mb-1 block">
            ← Clients
          </button>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-800">
              {client.client_type === 'company'
                ? client.company_name
                : `${client.first_name} ${client.last_name}`}
            </h2>
            <CopyLinkButton title="Copy link to this client's detail page" />
          </div>
          {client.client_type === 'company' && (
            <p className="text-gray-500">{client.first_name} {client.last_name}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              client.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {client.status}
            </span>
            <span className="text-sm text-gray-500 capitalize">{client.client_type}</span>
          </div>
        </div>
        <button
          onClick={() => setShowEditClient(!showEditClient)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          Edit Client
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-4 text-sm">{error}</div>
      )}

      {/* Edit Client Form */}
      {showEditClient && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Edit Client</h3>
          <form onSubmit={handleSaveClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor={fid('cd-client-type')} className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
              <select id={fid('cd-client-type')} value={editForm.client_type}
                onChange={e => setEditForm({...editForm, client_type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
            {editForm.client_type === 'company' && (
              <div>
                <label htmlFor={fid('cd-company')} className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input id={fid('cd-company')} type="text" value={editForm.company_name}
                  onChange={e => setEditForm({...editForm, company_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
            <div>
              <label htmlFor={fid('cd-first')} className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input id={fid('cd-first')} type="text" required value={editForm.first_name}
                onChange={e => setEditForm({...editForm, first_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-last')} className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input id={fid('cd-last')} type="text" required value={editForm.last_name}
                onChange={e => setEditForm({...editForm, last_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-email')} className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input id={fid('cd-email')} type="email" value={editForm.email}
                onChange={e => setEditForm({...editForm, email: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-email2')} className="block text-sm font-medium text-gray-700 mb-1">Secondary Email</label>
              <input id={fid('cd-email2')} type="email" value={editForm.secondary_email}
                onChange={e => setEditForm({...editForm, secondary_email: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-phone')} className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input id={fid('cd-phone')} type="text" value={editForm.phone}
                onChange={e => setEditForm({...editForm, phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-phone2')} className="block text-sm font-medium text-gray-700 mb-1">Secondary Phone</label>
              <input id={fid('cd-phone2')} type="text" value={editForm.secondary_phone}
                onChange={e => setEditForm({...editForm, secondary_phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor={fid('cd-address')} className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input id={fid('cd-address')} type="text" value={editForm.address}
                onChange={e => setEditForm({...editForm, address: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-city')} className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input id={fid('cd-city')} type="text" value={editForm.city}
                onChange={e => setEditForm({...editForm, city: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor={fid('cd-state')} className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input id={fid('cd-state')} type="text" value={editForm.state}
                  onChange={e => setEditForm({...editForm, state: e.target.value})}
                  placeholder="CA"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cd-zip')} className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                <input id={fid('cd-zip')} type="text" value={editForm.zip}
                  onChange={e => setEditForm({...editForm, zip: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label htmlFor={fid('cd-referred')} className="block text-sm font-medium text-gray-700 mb-1">Referred By</label>
              <input id={fid('cd-referred')} type="text" value={editForm.referred_by}
                onChange={e => setEditForm({...editForm, referred_by: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('cd-status')} className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select id={fid('cd-status')} value={editForm.status}
                onChange={e => setEditForm({...editForm, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="prospect">Prospect</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Save Changes
              </button>
              <button type="button" onClick={() => setShowEditClient(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Active Matters</p>
          <p className="text-2xl font-bold text-gray-800">{client.summary?.active_matters || 0}</p>
        </div>
        {client.summary?.wip_amount !== null && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">WIP</p>
            <p className="text-lg font-bold text-blue-600">
              ${parseFloat(client.summary?.wip_amount || 0).toFixed(2)}
            </p>
          </div>
        )}
        {client.summary?.total_billed !== null && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Total Billed</p>
            <p className="text-lg font-bold text-gray-800">
              ${parseFloat(client.summary?.total_billed || 0).toFixed(2)}
            </p>
          </div>
        )}
        {client.summary?.ar_balance !== null && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">AR Balance</p>
            <p className={`text-lg font-bold ${parseFloat(client.summary?.ar_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ${parseFloat(client.summary?.ar_balance || 0).toFixed(2)}
            </p>
          </div>
        )}
        {canViewTrust && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Trust Balance</p>
          <p className="text-lg font-bold text-green-600">
            ${parseFloat(client.summary?.trust_balance || 0).toFixed(2)}
          </p>
        </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Client Info */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Contact Information</h3>
            <dl className="space-y-3">
              {client.email && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Email</dt>
                  <dd className="text-sm text-gray-800">
                    <a href={`mailto:${client.email}`} className="text-primary-600 hover:underline">
                      {client.email}
                    </a>
                  </dd>
                </div>
              )}
              {client.secondary_email && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Secondary Email</dt>
                  <dd className="text-sm text-gray-800">{client.secondary_email}</dd>
                </div>
              )}
              {client.phone && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Phone</dt>
                  <dd className="text-sm text-gray-800">
                    <a href={`tel:${client.phone}`} className="text-primary-600 hover:underline">
                      {client.phone}
                    </a>
                  </dd>
                </div>
              )}
              {client.secondary_phone && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Secondary Phone</dt>
                  <dd className="text-sm text-gray-800">{client.secondary_phone}</dd>
                </div>
              )}
              {(client.address || client.city) && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Address</dt>
                  <dd className="text-sm text-gray-800">
                    {client.address && <div>{client.address}</div>}
                    {(client.city || client.state || client.zip) && (
                      <div>{[client.city, client.state, client.zip].filter(Boolean).join(', ')}</div>
                    )}
                  </dd>
                </div>
              )}
              {client.referred_by && (
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Referred By</dt>
                  <dd className="text-sm text-gray-800">{client.referred_by}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-400 uppercase">Client Since</dt>
                <dd className="text-sm text-gray-800">
                  {client.created_at ? format(new Date(client.created_at), 'MMM d, yyyy') : '—'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Upcoming Events */}
          {canViewCalendar && client.events?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Upcoming Events</h3>
              <div className="space-y-3">
                {client.events.map(event => (
                  <div key={event.id} className="flex items-start gap-3">
                    <div className="text-center min-w-[36px]">
                      <p className="text-xs text-gray-400">{fmtFirmClock(event.start_time, 'MMM')}</p>
                      <p className="text-lg font-bold text-primary-600">{fmtFirmClock(event.start_time, 'd') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{event.title}</p>
                      <p className="text-xs text-gray-500">{event.matter_title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Matters, Time, Invoices, Notes */}
        <div className="lg:col-span-2 space-y-6">
          {/* Matters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                Matters
                <span className="ml-2 text-sm text-gray-400">({client.matters?.length || 0})</span>
              </h3>
              <Link to={`/matters?new=1&client=${client.id}`} className="text-sm text-primary-600 hover:underline">
                + New Matter
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {client.matters?.length === 0 ? (
                <p className="text-gray-400 text-sm p-6">No matters yet.</p>
              ) : (
                client.matters?.map(matter => (
                  <Link key={matter.id} to={`/matters/${matter.id}`}
                    className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="font-medium text-gray-800">{matter.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {matter.file_number && (
                          <span className="text-xs text-gray-400 font-mono">#{matter.file_number}</span>
                        )}
                        {matter.practice_area && (
                          <span className="text-xs text-gray-400">{matter.practice_area}</span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          matter.status === 'active' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{matter.status}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      {canViewTimeEntries && parseFloat(matter.wip_amount) > 0 && (
                        <p className="text-sm font-medium text-blue-600">
                          WIP ${parseFloat(matter.wip_amount).toFixed(2)}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">→</p>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Recent Time Entries */}
          {canViewTimeEntries && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                Time Entries
                <span className="ml-2 text-sm text-gray-400">(last 10)</span>
              </h3>
              {canViewAll && (
                <button
                  onClick={() => setShowAllTime(!showAllTime)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                    showAllTime
                      ? 'bg-gray-700 text-white border-gray-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {showAllTime ? 'My Time Only' : 'View All Time'}
                </button>
              )}
            </div>
            <div className="p-6">
              {/* Hours Summary */}
              {client.time_entries?.length > 0 && (() => {
                const displayEntries = showAllTime && canViewAll
                  ? client.time_entries
                  : client.time_entries?.filter(e => e.user_id === user?.id)
                const posted = displayEntries?.filter(e => e.status === 'posted') || []
                const unposted = displayEntries?.filter(e => e.status === 'unposted') || []
                const billed = displayEntries?.filter(e => e.status === 'billed' || e.billed) || []
                return (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <p className="text-xs text-yellow-700 font-medium">Unposted</p>
                      <p className="text-lg font-bold text-yellow-700">
                        {unposted.reduce((sum, e) => sum + roundBillable(e.hours), 0).toFixed(1)} hrs
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      {/* Every posted hour, including no-charge — not "WIP". */}
                      <p className="text-xs text-blue-700 font-medium">Posted</p>
                      <p className="text-lg font-bold text-blue-700">
                        {posted.reduce((sum, e) => sum + roundBillable(e.hours), 0).toFixed(1)} hrs
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-green-700 font-medium">Billed</p>
                      <p className="text-lg font-bold text-green-700">
                        {billed.reduce((sum, e) => sum + roundBillable(e.hours), 0).toFixed(1)} hrs
                      </p>
                    </div>
                  </div>
                )
              })()}
              {client.time_entries?.length === 0 ? (
                <p className="text-gray-400 text-sm">No time entries yet.</p>
              ) : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Matter</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(showAllTime && canViewAll ? client.time_entries : client.time_entries?.filter(e => e.user_id === user?.id))?.map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {format(parseDateOnly(entry.date), 'MMM d')}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{entry.matter_title}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{entry.description}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{roundBillable(entry.hours).toFixed(1)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            entry.status === 'unposted' ? 'bg-yellow-100 text-yellow-700' :
                            entry.status === 'posted' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>{entry.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </div>
          )}

          {/* Invoices */}
          {canViewInvoices && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                Invoices
                <span className="ml-2 text-sm text-gray-400">({client.invoices?.length || 0})</span>
              </h3>
            </div>
            <div className="p-6">
              {client.invoices?.length === 0 ? (
                <p className="text-gray-400 text-sm">No invoices yet.</p>
              ) : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Invoice #</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Matter</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Total</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {client.invoices?.map(invoice => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-primary-600">{invoice.invoice_number}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{invoice.matter_title}</td>
                        <td className="px-3 py-2 text-right font-bold text-gray-800">
                          ${parseFloat(invoice.total).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>{invoice.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </div>
          )}

          {/* Notes */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                Notes
                <span className="ml-2 text-sm text-gray-400">({client.notes?.length || 0})</span>
              </h3>
            </div>
            <div className="p-6">
              {/* Add Note */}
              <form onSubmit={handleAddNote} className="mb-4">
                <textarea
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  placeholder="Add a note about this client..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button type="submit"
                  className="mt-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  Add Note
                </button>
              </form>

              {/* Notes List */}
              {client.notes?.length === 0 ? (
                <p className="text-gray-400 text-sm">No notes yet.</p>
              ) : (
                <div className="space-y-3">
                  {client.notes?.map(note => (
                    <div key={note.id} className="bg-gray-50 rounded-lg p-4">
                      {editingNote === note.id ? (
                        <div>
                          <textarea
                            value={editNoteContent}
                            onChange={e => setEditNoteContent(e.target.value)}
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => handleUpdateNote(note.id)}
                              className="text-xs text-green-600 hover:underline">Save</button>
                            <button onClick={() => setEditingNote(null)}
                              className="text-xs text-gray-500 hover:underline">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-400">
                              {note.created_by_name} • {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                            </p>
                            <div className="flex gap-2">
                              <button onClick={() => {
                                setEditingNote(note.id)
                                setEditNoteContent(note.content)
                              }} className="text-xs text-blue-600 hover:underline">Edit</button>
                              <button onClick={() => handleDeleteNote(note.id)}
                                className="text-xs text-red-500 hover:underline">Delete</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Portal Access — admin or portal_management */}
      {(user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.portal_management) && (
        <PortalAccessCard clientId={client.id} clientName={displayClientName(client)} />
      )}
    </div>
  )
}

function PortalAccessCard({ clientId, clientName }) {
  const ask = useAsk()
  const [status, setStatus] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [inviteModal, setInviteModal] = useState(null)
  // M-Cx1: wire the invite modal through useModal for Esc-close + focus trap
  // + return-focus discipline.  onClose fires when Esc pressed or backdrop
  // click; the actual state clear lives in a closeInvite() helper below.
  const closeInvite = () => setInviteModal(null)
  const { containerProps: inviteModalContainerProps } = useModal({
    enabled: !!inviteModal,
    onClose: closeInvite,
  })

  const load = async () => {
    try {
      const res = await api.get(`/api/clients/${clientId}/portal/status`)
      setStatus(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading portal status')
    }
  }

  useEffect(() => { load() }, [clientId])

  const sendInvite = async () => {
    setBusy(true); setError('')
    try {
      const res = await api.post(`/api/clients/${clientId}/portal/invite`, {})
      setInviteModal(res.data)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Error sending invite')
    } finally { setBusy(false) }
  }

  const revoke = async () => {
    if (!await ask({ type: 'confirm', title: 'Revoke portal access?', message: `Revoke portal access for ${clientName}?`, confirmLabel: 'Revoke', danger: true })) return
    setBusy(true); setError('')
    try {
      await api.delete(`/api/clients/${clientId}/portal/revoke`)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Error revoking access')
    } finally { setBusy(false) }
  }

  const reset2fa = async () => {
    if (!await ask({
      type: 'confirm',
      title: 'Reset 2FA?',
      message: `Reset two-factor authentication for ${clientName}?\n\nUse this only when the client has lost both their phone AND their backup codes.  After reset, they can sign in with password only, then re-enroll from their Profile.`,
      confirmLabel: 'Reset 2FA',
      danger: true,
    })) return
    setBusy(true); setError('')
    try {
      await api.post(`/api/clients/${clientId}/portal/2fa/reset`)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Error resetting 2FA')
    } finally { setBusy(false) }
  }

  const copy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {})
  }

  if (!status) return null

  const state = status.portal_enabled ? 'enabled' : (status.pending_invite ? 'invited' : 'disabled')
  const chip = {
    enabled:  'bg-green-100 text-green-700',
    invited:  'bg-yellow-100 text-yellow-700',
    disabled: 'bg-gray-100 text-gray-700'
  }[state]
  const label = { enabled: 'Enabled', invited: 'Invited (pending)', disabled: 'Disabled' }[state]

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">🔐 Portal Access</h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${chip}`}>{label}</span>
      </div>

      {error && <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-4 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <p className="text-xs text-gray-500 uppercase">Portal Email</p>
          <p className="font-medium">{status.portal_email || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Last Login</p>
          <p className="font-medium">{status.portal_last_login ? new Date(status.portal_last_login).toLocaleString() : 'Never'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Two-Factor Auth</p>
          <p className="font-medium">
            {status.totp_enabled ? (
              <span className="text-green-700">✓ Enabled{status.totp_enrolled_at ? ` (${new Date(status.totp_enrolled_at).toLocaleDateString()})` : ''}</span>
            ) : (
              <span className="text-gray-500">Not enabled</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={sendInvite} disabled={busy}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
          Send Portal Invite
        </button>
        {status.portal_enabled && status.totp_enabled && (
          <button onClick={reset2fa} disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
            title="Emergency reset — use only if client lost both phone and backup codes">
            Reset 2FA
          </button>
        )}
        {status.portal_enabled && (
          <button onClick={revoke} disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
            Revoke Access
          </button>
        )}
      </div>

      {status.activity?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Activity</h4>
          <ul className="divide-y divide-gray-100 text-sm">
            {status.activity.map((a, i) => (
              <li key={i} className="py-2 flex items-center justify-between text-xs">
                <span className="text-gray-700">{a.action.replace(/_/g, ' ')}</span>
                <span className="text-gray-400">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {inviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeInvite}>
          <div {...inviteModalContainerProps} aria-labelledby="portal-invite-title" className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 id="portal-invite-title" className="text-lg font-semibold text-gray-800 mb-2">Portal Invite Ready</h3>
            {inviteModal.email_sent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 mb-3">
                ✅ Invite email sent to <strong>{inviteModal.email}</strong>.  You can also copy the URL below and share it another way.
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 mb-3">
                📋 SMTP not configured on the server{inviteModal.email_reason ? ` (${inviteModal.email_reason})` : ''} — copy the URL below and email it to <strong>{clientName}</strong> yourself.  It expires in 7 days.  To enable automatic sending, set the SMTP_* env vars on the API container.
              </div>
            )}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono break-all mb-3">
              {inviteModal.invite_url}
            </div>
            <div className="flex gap-2">
              <button onClick={() => copy(inviteModal.invite_url)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                Copy Link
              </button>
              <button onClick={closeInvite}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}